import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/Categories.css';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'checkbox',
    order: 0,
    isRequired: false
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory._id}`, formData);
      } else {
        await api.post('/categories', { ...formData, order: categories.length });
      }
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', type: 'checkbox', order: 0, isRequired: false });
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.message || 'שגיאה בשמירה');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      type: category.type,
      order: category.order,
      isRequired: category.isRequired
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם למחוק קטגוריה זו?')) {
      try {
        await api.delete(`/categories/${id}`);
        fetchCategories();
      } catch (error) {
        alert('שגיאה במחיקה');
      }
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newCategories = [...categories];
    [newCategories[index - 1], newCategories[index]] = [newCategories[index], newCategories[index - 1]];
    
    const orders = newCategories.map((cat, i) => ({ id: cat._id, order: i }));
    try {
      await api.put('/categories/reorder/all', { orders });
      setCategories(newCategories);
    } catch (error) {
      alert('שגיאה בעדכון סדר');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === categories.length - 1) return;
    const newCategories = [...categories];
    [newCategories[index], newCategories[index + 1]] = [newCategories[index + 1], newCategories[index]];
    
    const orders = newCategories.map((cat, i) => ({ id: cat._id, order: i }));
    try {
      await api.put('/categories/reorder/all', { orders });
      setCategories(newCategories);
    } catch (error) {
      alert('שגיאה בעדכון סדר');
    }
  };

  if (loading) {
    return <div className="loading">טוען...</div>;
  }

  return (
    <div className="categories-page">
      <div className="page-header">
        <h1>ניהול קטגוריות</h1>
        <button className="btn-primary" onClick={() => {
          setEditingCategory(null);
          setFormData({ name: '', type: 'checkbox', order: 0, isRequired: false });
          setShowModal(true);
        }}>
          + קטגוריה חדשה
        </button>
      </div>

      <div className="categories-list">
        {categories.map((category, index) => (
          <div key={category._id} className="category-card">
            <div className="category-order">
              <button 
                className="btn-arrow" 
                onClick={() => handleMoveUp(index)}
                disabled={index === 0}
              >
                ▲
              </button>
              <span>{index + 1}</span>
              <button 
                className="btn-arrow" 
                onClick={() => handleMoveDown(index)}
                disabled={index === categories.length - 1}
              >
                ▼
              </button>
            </div>
            
            <div className="category-info">
              <h3>{category.name}</h3>
              <div className="category-tags">
                <span className={`tag tag-${category.type}`}>
                  {category.type === 'dropdown' ? 'רשימה נפתחת (בחירה אחת)' : 'בחירה מרובה'}
                </span>
                {category.isRequired && <span className="tag tag-required">חובה</span>}
              </div>
            </div>
            
            <div className="category-actions">
              <button className="btn-edit" onClick={() => handleEdit(category)}>ערוך</button>
              <button className="btn-delete" onClick={() => handleDelete(category._id)}>מחק</button>
            </div>
          </div>
        ))}
        
        {categories.length === 0 && (
          <div className="no-data">
            <p>אין קטגוריות עדיין</p>
            <p>הוסף קטגוריות כדי לארגן את המוצרים במחשבון</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingCategory ? 'עריכת קטגוריה' : 'קטגוריה חדשה'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>שם הקטגוריה</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="לדוגמה: סוג עגלה, אביזרים, מקלדות..."
                  required
                />
              </div>
              
              <div className="form-group">
                <label>סוג בחירה</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="dropdown">רשימה נפתחת (בחירה אחת)</option>
                  <option value="checkbox">בחירה מרובה (צ'קבוקס)</option>
                </select>
                <p className="form-hint">
                  {formData.type === 'dropdown' 
                    ? 'המשתמש יוכל לבחור מוצר אחד בלבד מהקטגוריה' 
                    : 'המשתמש יוכל לבחור מספר מוצרים מהקטגוריה'}
                </p>
              </div>
              
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isRequired}
                    onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                  />
                  חובה לבחור מקטגוריה זו
                </label>
              </div>
              
              <div className="modal-buttons">
                <button type="submit" className="btn-primary">שמור</button>
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>ביטול</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;